import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  Calendar,
  Clock,
  Scissors,
  MapPin,
  ArrowRight,
  AlertCircle,
  CheckCircle2,
  XCircle,
} from 'lucide-react';
import { toast } from 'react-toastify';
import { bookingService } from '../../api';
import { openAuthModal } from '../../utils/openAuthModal';
import styles from './bookings.module.css';

export default function BookingsPage() {
  const navigate = useNavigate();
  const user = useSelector((s) => s.auth?.user);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(Boolean(user));

  useEffect(() => {
    if (!user) {
      setBookings([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    bookingService
      .getMyBookings()
      .then((data) => {
        setBookings(Array.isArray(data) ? data : []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [user]);

  const handleCancel = async (id) => {
    if (!window.confirm('Are you sure you want to cancel this appointment slot?')) return;
    try {
      await bookingService.cancelBooking(id);
      setBookings((b) =>
        b.map((item) => (item._id === id ? { ...item, status: 'CANCELLED' } : item))
      );
      toast.success('Appointment cancelled successfully.');
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Failed to cancel appointment.');
    }
  };

  const getStatusBadge = (status) => {
    const s = String(status || '').toUpperCase();
    switch (s) {
      case 'CONFIRMED':
        return (
          <span className={`${styles.statusBadge} ${styles.statusConfirmed}`}>
            <CheckCircle2 size={11} /> Confirmed
          </span>
        );
      case 'COMPLETED':
        return (
          <span className={`${styles.statusBadge} ${styles.statusCompleted}`}>
            <CheckCircle2 size={11} /> Completed
          </span>
        );
      case 'CANCELLED':
        return (
          <span className={`${styles.statusBadge} ${styles.statusCancelled}`}>
            <XCircle size={11} /> Cancelled
          </span>
        );
      default:
        return (
          <span className={`${styles.statusBadge} ${styles.statusPending}`}>
            <AlertCircle size={11} /> {status || 'Pending'}
          </span>
        );
    }
  };

  if (!user) {
    return (
      <div className={styles.pageContainer}>
        <div className={styles.emptyCard}>
          <Calendar size={44} style={{ color: '#94a3b8', marginBottom: '8px' }} />
          <h2
            style={{ fontSize: '1.25rem', fontWeight: 800, color: '#0f172a', margin: '0 0 6px 0' }}
          >
            Sign In to View Appointments
          </h2>
          <p
            style={{
              fontSize: '0.85rem',
              color: '#64748b',
              maxWidth: 360,
              margin: '0 auto 1.5rem auto',
            }}
          >
            Track scheduled slots, review treatments, and manage bookings across all your favorite
            salons.
          </p>
          <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
            <button
              type="button"
              className={styles.btnPrimary}
              onClick={() => openAuthModal('signin')}
            >
              Sign In
            </button>
            <button
              type="button"
              className={styles.btnViewShop}
              onClick={() => navigate('/salons')}
            >
              Browse Salons
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.pageContainer}>
      {/* Header */}
      <div className={styles.header}>
        <h1 className={styles.title}>My Appointments</h1>
        <p className={styles.subtitle}>
          Supervise your scheduled salon visits and booking history.
        </p>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '3rem', color: '#64748b' }}>
          <Scissors size={28} style={{ color: '#dc2626', marginBottom: '8px' }} />
          <p>Loading your appointments…</p>
        </div>
      ) : bookings.length === 0 ? (
        <div className={styles.emptyCard}>
          <Calendar size={44} style={{ color: '#94a3b8', marginBottom: '8px' }} />
          <h2
            style={{ fontSize: '1.15rem', fontWeight: 700, color: '#0f172a', margin: '0 0 6px 0' }}
          >
            No Bookings Yet
          </h2>
          <p style={{ fontSize: '0.825rem', color: '#64748b', margin: '0 0 1.25rem 0' }}>
            You haven't scheduled any salon appointments. Book top-rated stylists near you.
          </p>
          <Link to="/salons" className={styles.btnPrimary}>
            <span>Explore Salons</span>
            <ArrowRight size={14} />
          </Link>
        </div>
      ) : (
        <div className={styles.bookingsList}>
          {bookings.map((b) => {
            const shopName = b.shop?.name || b.shopId?.name || 'Barbershop';
            const shopId = b.shop?._id || b.shopId?._id || b.shop;
            const serviceName = b.service?.name || b.product?.name || 'Salon Treatment';
            const price = b.service?.price ?? b.product?.price ?? b.amount ?? 0;
            const isCancelled = String(b.status || '').toUpperCase() === 'CANCELLED';
            const isCompleted = String(b.status || '').toUpperCase() === 'COMPLETED';

            return (
              <div key={b._id} className={styles.bookingCard}>
                <div className={styles.cardHeader}>
                  <div>
                    <h3 className={styles.shopName}>{shopName}</h3>
                    {b.shop?.address?.city && (
                      <div className={styles.shopAddress}>
                        <MapPin size={12} style={{ color: '#dc2626' }} />
                        <span>{b.shop.address.city}</span>
                      </div>
                    )}
                  </div>
                  <div>{getStatusBadge(b.status)}</div>
                </div>

                <div className={styles.serviceDetails}>
                  <div className={styles.serviceRow}>
                    <span className={styles.serviceName}>
                      <Scissors size={14} style={{ color: '#dc2626' }} />
                      <span>{serviceName}</span>
                    </span>
                    <span className={styles.servicePrice}>₹{Number(price).toLocaleString()}</span>
                  </div>

                  <div className={styles.metaRow}>
                    <div className={styles.metaItem}>
                      <Calendar size={13} />
                      <span>
                        {b.date
                          ? new Date(b.date).toLocaleDateString('en-IN', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric',
                            })
                          : 'Scheduled'}
                      </span>
                    </div>
                    {b.slot && (
                      <div className={styles.metaItem}>
                        <Clock size={13} />
                        <span>{b.slot}</span>
                      </div>
                    )}
                  </div>
                </div>

                <div className={styles.cardFooter}>
                  <div>
                    {shopId && (
                      <button
                        type="button"
                        className={styles.btnViewShop}
                        onClick={() => navigate(`/salon/${shopId}`)}
                      >
                        Visit Salon Page
                      </button>
                    )}
                  </div>

                  {!isCancelled && !isCompleted && (
                    <button
                      type="button"
                      className={styles.btnCancel}
                      onClick={() => handleCancel(b._id)}
                    >
                      Cancel Appointment
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
